import Vue from 'vue';

/**
 * @typedef {Object<string, string|number>} Country
 * @property {number} country_id Country identifier.
 * @property {string} country_name Country name.
 * @property {string} iso2 Two-letter country codes.
 * @property {string} iso3 Three-letter country codes.
 */
/**
 * @typedef {Object<string, string>} State
 * @property {string} country_iso3 Three-letter country codes.
 * @property {string} state State name.
 * @property {string|number} state_iso2 Two-letter or two-digit state codes.
 */
/**
 * @typedef {Object<string, string>} City
 * @property {string} city_name City name.
 * @property {string} country_iso3 Three-letter country codes.
 * @property {number} geoname_id Geo identifier.
 * @property {string} state State name.
 * @property {string|number} state_iso2 Two-letter or two-digit state codes.
 */
/**
 * @typedef {Object<string, any>} DefaultPagination
 * @property {number} current_page
 * @property {number} per_page
 * @property {number} total
 * @property {number} total_pages
 */
/**
 * @template T
 * @typedef {Object<string, any>} GetDefault
 * @property {T[]} data An array of Country
 * @property {DefaultPagination} meta.pagination
 */
export class GeoService {
  constructor() {
    this._url = '/v2/geo';
    this._axios = Vue.prototype.$axios;
  }

  set axios(value) {
    this._axios = value;
  }

  /**
   * Return an array of countries.
   *
   * @return {Promise<GetDefault<Country>>}
   */
  async getCountries() {
    const response = await this._axios.get(`${this._url}/countries`);
    return response.data;
  }

  /**
   * Return an array of state by country iso3.
   *
   * @param {string} iso3 Three-letter country codes.
   *
   * @return {Promise<GetDefault<State>>}
   */
  async getStateByCountryISO(iso3) {
    const params = {selectedCountries: iso3};
    const response = await this._axios.get(`${this._url}/regions`, {params});
    return response.data;
  }

  /**
   * Return an array of cities by country iso3 and state iso2.
   *
   * @param {string} country_iso3 Three-letter country codes.
   * @param {string|number|null} [state_iso2=null] Two-letter state codes.
   *
   * @return {Promise<GetDefault<City>>}
   */
  async getCitiesByISO(country_iso3, state_iso2 = null) {
    const params = {selectedCountries: country_iso3, selectedRegions: state_iso2};
    const response = await this._axios.get(`${this._url}/cities`, {params});
    return response.data;
  }
}
